import { Notify } from "quasar";
import {
  getCourses,
  getCoursesByUid,
  getMetadata,
} from "@/apis/course-service";
import {
  getAllAggs,
  getReviewsCourseIdsByUid,
} from "@/apis/course-rating-service";
import { getNotesCourseIdsByUid } from "@/apis/course-notes-service";
import { addOrGetCourseServiceUser } from "@/apis/user-service";
import * as fb from "@/firebaseConfig";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import Vue from "vue";
import Vuex from "vuex";
import { firestoreAction, vuexfireMutations } from "vuexfire";
import {
  DEFAULT_FILTER_COLUMNS,
  PREMIUM_DATA_MASK,
  USER_RATING_MIN,
  USER_RATING_MAX,
  RECENTLY_ADDED_THRESHOLD_DAYS,
  DATAPOINT_ID_MAP,
  LENGTH_UNITS,
  HEIGHT_UNITS,
  DISTANCE_UNITS,
  DEFAULT_MEASUREMENT_SYSTEM,
  DEFAULT_CURRENCY,
  DEFAULT_COUNTRY,
} from "@/constants";
import { logAnalyticsEvent } from "@/apis/analytics-service";
import { getFormattedCurrencyWithConversion } from "@/apis/exchange-rate-service";
import COUNTRIES from "@/data/countries.json";
import { dedupe } from "@/utils";

const dateLocale = "en-US";
const dateFormatOptions = {
  year: "numeric",
  // using 2-digit as opposed to numeric will
  // force a leading zero if applicable
  day: "2-digit",
  month: "2-digit",
};
const dateAddedFormatter = new Intl.DateTimeFormat(
  dateLocale,
  dateFormatOptions,
);

// tell Vue we plan to use Vuex
Vue.use(Vuex);

// the Vuex store we're going to be using in our app
// https://vuex.vuejs.org/#what-is-vuex
export const store = new Vuex.Store({
  state: {
    isLoading: false,
    locale: "en-US",
    // will eventually hold our metadata items
    courseMetadata: [],
    coursesFree: [],
    coursesPremium: [],
    // will eventually contain our Firebase Auth user object
    // bare-bones subset of user info as provided by Firebase Auth
    currentUser: null,
    // will hold an item for each filter added, e.g.
    // [
    //   {
    //     "id": "majorPgaEventVenue",
    //     "filter": [
    //       "Y"
    //     ],
    //     "type": "checkboxlist",
    //     "label": "Major PGA Event Venue"
    //   },
    //   {
    //     "id": "type",
    //     "filter": [
    //       "Private",
    //       "Resort"
    //     ],
    //     "type": "checkboxlist",
    //     "label": "Type"
    //   }
    // ]
    filterInput: [],
    // the columns to display in the list view, aka the results table
    // will default to below but can be modified
    filterColumns: DEFAULT_FILTER_COLUMNS,
    // will eventually hold the Firebase Auth user's token once loaded
    // used for validation with our back end REST API
    idToken: null,
    // will eventually contain additional user profile info about our user
    // we retrieve this from the Firestore `users` collection and we retrieve the proper record
    // by mapping the document id to the user's uid
    // will always remain in sync with Firestore because we are using Vuexfire, we set up the mapping in `bindUserProfileRef`
    // https://vuefire.vuejs.org/vuexfire/
    userProfile: {},
    // will eventually contain a list of all userProfiles
    // used by the user administration page
    userProfiles: [],
    // whether or not to filter by wishlist
    wishlistOnly: false,
    // whether or not to filter by course played
    coursePlayedOnly: false,
    // whether or not to filter by courses with personal notes
    notesOnly: false,
    // whether or not to filter by courses with personal ratings/reviews
    ratedOnly: false,
    // whether or not to filter by recently added courses
    recentlyAdded: false,
    // whether or not to filter by preferred country courses
    preferredCountryOnly: false,
    // whether or not to filter by non preferred country (international) courses
    internationalOnly: false,
    // whether or not to display quasar filter items in dense mode
    denseFilter: false,
    // whether or not to display the list view in dense mode
    denseListView: false,
    // will eventually contain the course id of the hovered course in map or list view
    hoveredCourseId: "",
    // will eventually contain the course id of the clicked course in map or list view
    clickedCourseId: "",
    wishlistEnabled: false,
    geoLocation: null,
    courseServiceUser: null,
    weatherFilterInput: null,
    weatherFilterZips: null,
    validSubscriptionTerm: false,
    // Filters courses within current viewable map
    smartZoom: false,
    // will eventually hold all course rating aggs
    courseRatingAggs: null,
    // will eventually hold the bounds of the map when smart zoom is enabled
    // used to restore the user to their last map position
    mapBounds: null,
    userCourseNotesCourseIds: [],
    userCourseReviewsCourseIds: [],
    listViewCurrencyColumns: ["OLGDP012", "OLGDP098"],
    mapVisited: false,
  },
  getters: {
    // helper to grab our user's uid as a string
    uid: state => {
      if (!state.currentUser) {
        return null;
      }

      return state.currentUser.uid;
    },
    isPremium: state => {
      const currentUser = state.currentUser;
      const coursesPremium = state.coursesPremium;

      if (!currentUser) {
        return false;
      }

      const premium = currentUser && coursesPremium.length > 0;

      return premium;
    },
    // an array all data points marked to be included in the filter
    // we create a list for each filter type of all items in the filter
    // we need to remove duplicates from this list, we then add it as the `allItems` property
    // if the filter is a range, we store the min and max values to be used by the range sliders
    filterDatapointsFormatted: (state, getters) => {
      const courses = getters.courses;
      const housingIndexSortPriority = ["Very Low", "Low", "Medium", "High"];
      const datapoints = state.courseMetadata
        .filter(m => m.includeInScreener)
        .map(m => {
          const allItems = courses.map(course => course[m.id]);
          const deDupedItems = dedupe(allItems);

          if (m.sortType === "number") {
            deDupedItems.sort((a, b) => a - b);
          } else if (m.id === "OLGDP087") {
            // OLGDP087 is housingIndex
            deDupedItems.sort((a, b) => {
              return (
                housingIndexSortPriority.indexOf(a) -
                housingIndexSortPriority.indexOf(b)
              );
            });
          } else {
            deDupedItems.sort();
          }

          const newDatapoint = {
            ...m,
            allItems: deDupedItems,
          };

          if (m.filterType === "range") {
            newDatapoint.min = minValue(deDupedItems);
            newDatapoint.max = maxValue(deDupedItems);
          } else if (m.filterType === "rating_range") {
            newDatapoint.min = USER_RATING_MIN;
            newDatapoint.max = USER_RATING_MAX;
          }

          return newDatapoint;
        });

      return datapoints;
    },
    filterDatapoints: (state, getters) => {
      const datapoints = getters.filterDatapointsFormatted.map(m => {
        const newDatapoint = {
          ...m,
        };
        const existingFilterInput = state.filterInput.find(
          fi => fi.id === m.id,
        );

        if (existingFilterInput) {
          const stringified = JSON.stringify(existingFilterInput.filter);
          const parsed = JSON.parse(stringified);

          newDatapoint.filter = parsed;
        }

        return newDatapoint;
      });

      return datapoints;
    },
    // https://lodash.com/docs/4.17.15#groupBy
    // we use the groupBy method from LoDash to group our filter datapoints by their category property
    // an object where each property is the category name and its value is an array of filter datapoints
    filterDatapointsGroupedByCategory: (state, getters) => {
      return groupBy(getters.filterDatapoints, "category");
    },
    // an array of each Filter category
    filterCategories: (state, getters) => {
      const sortPriority = [
        "Course Info",
        "Experience",
        "User Ratings",
        "Walkability",
        "Location",
        "Architecture",
        "Rankings",
        "Tournaments",
      ];
      const keys = Object.keys(getters.filterDatapointsGroupedByCategory);
      const categories = keys.sort((a, b) => {
        return sortPriority.indexOf(a) - sortPriority.indexOf(b);
      });

      return categories;
    },
    filterCategoriesWithActiveFilters: (state, getters) => {
      const filterInputDataPoints = dedupe(state.filterInput.map(fi => fi.id));
      const filterInputCategories = dedupe(
        filterInputDataPoints.map(
          fidp => getters.filterDatapoints.find(dp => dp.id === fidp).category,
        ),
      );

      return filterInputCategories;
    },
    courses: (state, getters) => {
      const coursesFree = state.coursesFree;
      const coursesPremium = state.coursesPremium;
      const courseRatingAggs = state.courseRatingAggs;
      const listViewCurrencyColumns = state.listViewCurrencyColumns;
      const courses = coursesFree.map(currentCourse => {
        const targetCourse = coursesPremium.find(
          pc => pc.id === currentCourse.id,
        );

        if (courseRatingAggs !== null) {
          courseRatingAggs.forEach(category => {
            const results = category.results;
            const foundResults = results.find(
              result => result.courseId === currentCourse.id,
            );

            if (foundResults) {
              const { courseId, ...ratings } = foundResults;

              currentCourse = {
                ...currentCourse,
                ...ratings,
              };
            }
          });
        }

        const course = {
          ...currentCourse,
          ...targetCourse,
        };

        // add converted currency columns
        listViewCurrencyColumns.forEach(dpId => {
          const amt = course[dpId];
          const courseCurrency = course.OLGDP103;

          if (amt === PREMIUM_DATA_MASK) {
            return;
          }

          if (!amt) {
            return;
          }

          if (!courseCurrency) {
            return;
          }

          const preferredCurrency = getters.preferredCurrency;
          const defaultCurrency = DEFAULT_CURRENCY;
          const result = getFormattedCurrencyWithConversion(
            amt,
            courseCurrency,
            preferredCurrency,
            defaultCurrency,
          );
          const { converted, formatted } = result;

          course[`${dpId}_converted`] = converted;
          course[`${dpId}_formatted`] = formatted;
        });

        // add formatted length column (Yardage)
        ["OLGDP006", "OLGDP142", "OLGDP118", "OLGDP126"].forEach(dpId => {
          if (course[dpId]) {
            const id = dpId;
            const preferredUnit = getters.preferredLengthUnit;
            let val = course[id];

            if (preferredUnit === "meter") {
              val = convertYardToMeter(val);
            }

            const unitFormatter = getters.lengthUnitFormatter;
            const formatted = unitFormatter.format(val);

            course[`${id}_formatted`] = formatted;
          }
        });
        if (course.OLGDP092) {
          const id = "OLGDP092";
          const preferredUnit = getters.preferredHeightUnit;
          let val = course[id];

          if (preferredUnit === "meter") {
            val = convertFootToMeter(val);
          }

          const unitFormatter = getters.heightUnitFormatter;
          const formatted = unitFormatter.format(val);

          course[`${id}_formatted`] = formatted;
        }

        // add formatted Total Ascent
        if (course.OLGDP093) {
          const id = "OLGDP093";
          const preferredUnit = getters.preferredHeightUnit;
          let val = course[id];

          if (preferredUnit === "meter") {
            val = convertFootToMeter(val);
          }

          const unitFormatter = getters.heightUnitFormatter;
          const formatted = unitFormatter.format(val);

          course[`${id}_formatted`] = formatted;
        }

        // add formatted Total Walking Distance
        if (course.OLGDP094) {
          const id = "OLGDP094";
          const preferredUnit = getters.preferredDistanceUnit;
          let val = course[id];

          if (preferredUnit === "kilometer") {
            val = convertMileToKilometer(val);
          }

          const unitFormatter = getters.distanceUnitFormatter;
          const formatted = unitFormatter.format(val);

          course[`${id}_formatted`] = formatted;
        }

        return course;
      });

      return courses;
    },
    // an array of courses that pass the filters
    filteredCoursesAll: (state, getters) => {
      let courses = getters.courses;
      const preferredCountryCourses = getters.preferredCountryCourses;
      const internationalCourses = getters.internationalCourses;

      function getSharedCoursesInLists(courseList1, courseList2) {
        return courseList1.filter(course1 =>
          courseList2.some(course2 => course1.id === course2.id),
        );
      }

      if (state.wishlistOnly) {
        courses = getters.wishlistCourses;
      } else if (state.coursePlayedOnly) {
        courses = getters.coursePlayedCourses;
      } else if (state.notesOnly) {
        courses = getters.notesCourses;
      } else if (state.ratedOnly) {
        courses = getters.reviewsCourses;
      } else if (state.recentlyAdded) {
        courses = getters.recentlyAddedCourses;
      }

      if (state.preferredCountryOnly) {
        courses = getSharedCoursesInLists(courses, preferredCountryCourses);
      } else if (state.internationalOnly) {
        courses = getSharedCoursesInLists(courses, internationalCourses);
      }

      let filtered = courses.filter(course => {
        const passesAllFilters = state.filterInput.every(filterItem =>
          courseFilterFn(course, filterItem),
        );

        return passesAllFilters;
      });

      if (state.weatherFilterZips) {
        const zips = state.weatherFilterZips;

        filtered = filtered.filter(course => {
          // OLGDP112 is postalCode
          const isCourseInZips = zips.includes(course.OLGDP112);

          return isCourseInZips;
        });
      }

      return filtered;
    },
    filteredCourses: (state, getters) => {
      const courses = getters.filteredCoursesAll;
      const mapBounds = state.mapBounds;
      const smartZoom = state.smartZoom;

      if (!mapBounds) {
        return courses;
      }

      if (!smartZoom) {
        return courses;
      }

      const filtered = courses.filter(course => {
        const lat = course.latitude;
        const lng = course.longitude;
        const latLng = {
          lat,
          lng,
        };
        const isInBounds = mapBounds.contains(latLng);

        return isInBounds;
      });

      return filtered;
    },
    // an array of columns to be available in the list view, aka the results table
    listViewColumnsConfig: state => {
      return state.courseMetadata
        .filter(dataPoint => dataPoint.includeInListView)
        .map(dataPoint => {
          const columnConfig = {
            align: dataPoint.cellAlign,
            field: dataPoint.id,
            name: dataPoint.id,
            label: dataPoint.label,
            labelLVCM: dataPoint.labelLVCM,
            sortable: !!dataPoint.sortable,
            description: dataPoint.description,
            category: dataPoint.category,
          };

          // special handling for the course name col
          if (["OLGDP001"].includes(dataPoint.id)) {
            columnConfig.classes = "col--course-id";
            columnConfig.headerClasses = "col--course-id";
          }

          // columns with custom formatting:
          // yardage, maxFee, powerCartFee,
          // elevation, totalAscent, totalWalkingDistance
          if (
            [
              "OLGDP006",
              "OLGDP012",
              "OLGDP098",
              "OLGDP092",
              "OLGDP093",
              "OLGDP094",
            ].includes(dataPoint.id)
          ) {
            const formatFn = (val, row) => {
              if (!val) {
                return "";
              }

              if (val === PREMIUM_DATA_MASK) {
                return val;
              }

              return row[`${dataPoint.id}_formatted`];
            };

            if (["OLGDP012", "OLGDP098"].includes(dataPoint.id)) {
              columnConfig.field = row => row[`${dataPoint.id}_converted`];
            }

            columnConfig.format = formatFn;
          }

          // dateAdded
          if (["OLGDP100"].includes(dataPoint.id)) {
            const formatFn = val => {
              if (!val) {
                return "";
              }

              return dateAddedFormatter.format(val);
            };

            columnConfig.format = formatFn;
          }

          // as in USGA Course Rating
          // https://support.golfpadgps.com/support/solutions/articles/123-what-are-course-rating-and-slope-
          // not related to user ratings
          if (dataPoint.id === "OLGDP008") {
            const formatFn = val => {
              if (!val) {
                return "";
              }

              return val.toFixed(1);
            };

            columnConfig.format = formatFn;
          }

          // related to user ratings
          if (dataPoint.rating) {
            const dataType = dataPoint.dataType;
            const precision = dataPoint.precision;

            const formatFn = val => {
              if (!val) {
                return "";
              }

              if (dataType === "integer") {
                return parseInt(val, 10);
              } else if (dataType === "float") {
                return val.toFixed(precision);
              }
            };

            columnConfig.format = formatFn;
          }

          // we want to use the list view specific label if applicable
          if (dataPoint.labelListView) {
            columnConfig.label = dataPoint.labelListView;
          }

          return columnConfig;
        });
    },
    listViewColumnsGroupedByCategory: (state, getters) => {
      return groupBy(getters.listViewColumnsConfig, "category");
    },
    listViewColumnsCategories: (state, getters) => {
      return Object.keys(getters.listViewColumnsGroupedByCategory);
    },
    // an array of columns that have the sortType of number
    // we use this to know when we need to apply custom sorting logic
    listViewNumericColumns: state => {
      return state.courseMetadata
        .filter(m => m.sortType === "number")
        .map(m => m.id);
    },
    listViewDateColumns: state => {
      return state.courseMetadata
        .filter(m => m.sortType === "date")
        .map(m => m.id);
    },
    // an array of data points to be shown in the course view, aka the map dialog
    courseViewDatapoints: state => {
      return state.courseMetadata.filter(m => m.includeInCourseView);
    },
    // an array of data points to be shown in the mini course view, aka the hover profile
    miniCourseViewDatapoints: state => {
      return state.courseMetadata.filter(m => m.includeInMiniCourseView);
    },
    // an array of data points to be shown in the full course profile
    courseProfileDatapoints: state => {
      return state.courseMetadata.filter(m => m.includeInCourseProfile);
    },
    // an array of courseIds that make up the user's wishlist
    wishlistCourseIds: (state, getters) => {
      return getters.wishlist || [];
    },
    // an array of courses looked up from the list of ids
    wishlistCourses: (state, getters) => {
      const courses = getters.courses;

      return courses.filter(course =>
        getters.wishlistCourseIds.includes(course.id),
      );
    },
    // the user's wishlist stored in Firestore
    // an array of courseIds
    wishlist: state => {
      if (!state.userProfile) {
        return [];
      }

      return state.userProfile.wishlist;
    },
    // an array of courseIds that make up the user's course played
    coursePlayedCourseIds: (state, getters) => {
      return getters.coursePlayed || [];
    },
    // an array of courses looked up from the list of ids
    coursePlayedCourses: (state, getters) => {
      const courses = getters.courses;

      return courses.filter(course =>
        getters.coursePlayedCourseIds.includes(course.id),
      );
    },
    // the user's course played stored in Firestore
    // an array of courseIds
    coursePlayed: state => {
      if (!state.userProfile) {
        return [];
      }

      return state.userProfile.coursePlayed;
    },
    notesCourses: (state, getters) => {
      const courses = getters.courses;

      return courses.filter(course =>
        state.userCourseNotesCourseIds.includes(course.id),
      );
    },
    reviewsCourses: (state, getters) => {
      const courses = getters.courses;

      return courses.filter(course =>
        state.userCourseReviewsCourseIds.includes(course.id),
      );
    },
    recentlyAddedCourses: (state, getters) => {
      const today = new Date();
      const recentlyAddedCutoffDate = new Date(
        new Date().setDate(today.getDate() - RECENTLY_ADDED_THRESHOLD_DAYS),
      );
      const courses = getters.courses;

      return courses.filter(
        course => new Date(course.OLGDP100) >= recentlyAddedCutoffDate,
      );
    },
    preferredCountryCourses: (state, getters) => {
      const courses = getters.courses;
      const preferredCountry = getters.preferredCountry || DEFAULT_COUNTRY;
      const countryEntry = COUNTRIES.find(c => c.code === preferredCountry);
      const countryName = countryEntry.name;

      return courses.filter(course => course.OLGDP101 === countryName);
    },
    internationalCourses: (state, getters) => {
      const courses = getters.courses;
      const preferredCountry = getters.preferredCountry || DEFAULT_COUNTRY;
      const countryEntry = COUNTRIES.find(c => c.code === preferredCountry);
      const countryName = countryEntry.name;

      return courses.filter(course => course.OLGDP101 !== countryName);
    },
    // the user's saved columns stored in Firestore
    // an array of columnIds
    savedColumns: state => {
      if (!state.userProfile) {
        return [];
      }

      return state.userProfile.savedColumns;
    },
    // the user's list of authEvents
    // an array of login/logout events
    authEvents: state => {
      if (!state.userProfile) {
        return [];
      }

      return state.userProfile.authEvents;
    },
    // if the user is marked as admin in Firestore
    // we still use token validation on the back end so this is mostly a convenience
    // we use this to drive display of the User Administration link
    isAdmin: state => {
      if (!state.userProfile) {
        return false;
      }

      return state.userProfile.isAdmin || false;
    },
    lastGeolocation: state => {
      if (!state.userProfile) {
        return null;
      }

      return state.userProfile.lastGeolocation;
    },
    // the user's preferred country stored in Firestore
    preferredCountry: state => {
      if (!state.userProfile) {
        return null;
      }

      return state.userProfile.preferredCountry;
    },
    // the user's preferred currency stored in Firestore
    preferredCurrency: state => {
      if (!state.userProfile) {
        return null;
      }

      return state.userProfile.preferredCurrency;
    },
    // the user's preferred measurement system stored in Firestore
    preferredMeasurementSystem: state => {
      if (!state.userProfile) {
        return null;
      }

      if (!state.userProfile.preferredMeasurementSystem) {
        return DEFAULT_MEASUREMENT_SYSTEM.id;
      }

      return state.userProfile.preferredMeasurementSystem;
    },
    preferredLengthUnit: (state, getters) => {
      const system = getters.preferredMeasurementSystem;
      const unit = LENGTH_UNITS.find(u => u.system === system);

      return unit.id;
    },
    preferredHeightUnit: (state, getters) => {
      const system = getters.preferredMeasurementSystem;
      const unit = HEIGHT_UNITS.find(u => u.system === system);

      return unit.id;
    },
    preferredDistanceUnit: (state, getters) => {
      const system = getters.preferredMeasurementSystem;
      const unit = DISTANCE_UNITS.find(u => u.system === system);

      return unit.id;
    },
    lengthUnitFormatter: (state, getters) => {
      const locale = state.locale;
      const unit = getters.preferredLengthUnit;
      const formatter = new Intl.NumberFormat(locale, {
        style: "unit",
        unit,
        maximumFractionDigits: 0,
      });

      return formatter;
    },
    heightUnitFormatter: (state, getters) => {
      const locale = state.locale;
      const unit = getters.preferredHeightUnit;
      const formatter = new Intl.NumberFormat(locale, {
        style: "unit",
        unit,
        maximumFractionDigits: 0,
      });

      return formatter;
    },
    distanceUnitFormatter: (state, getters) => {
      const locale = state.locale;
      const unit = getters.preferredDistanceUnit;
      const formatter = new Intl.NumberFormat(locale, {
        style: "unit",
        unit,
        maximumFractionDigits: 2,
      });

      return formatter;
    },
  },
  mutations: {
    ...vuexfireMutations,
    setCurrentUser(state, val) {
      state.currentUser = val;
    },
    setUserProfiles(state, val) {
      state.userProfiles = val;
    },
    setCourseMetadata(state, val) {
      state.courseMetadata = val;
    },
    setCoursesFree(state, val) {
      state.coursesFree = val;
    },
    setCoursesPremium(state, val) {
      state.coursesPremium = val;
    },
    setFilterInput(state, val) {
      state.filterInput = val;
    },
    setFilterColumns(state, val) {
      if (!Array.isArray(val)) {
        return;
      }

      // Maps legacy style column ids like 'course' to datapoint ids like 'OLGDP001'.
      // This is a temporary measure and can be removed after INTL goes live and all Firebase users have been updated.
      // TODO: Write script to update all users and run it after INTL goes live.
      const newFilterColumns = val.map(filterColumn => {
        let dp = DATAPOINT_ID_MAP.find(d => d.aliases.includes(filterColumn));

        if (dp) {
          return dp.id;
        }

        return filterColumn;
      });

      console.debug("setFilterColumns", { val, newFilterColumns });

      state.filterColumns = newFilterColumns;
    },
    setIsLoading(state, val) {
      state.isLoading = val;
    },
    setWishlistOnly(state, val) {
      state.wishlistOnly = val;
    },
    setCoursePlayedOnly(state, val) {
      state.coursePlayedOnly = val;
    },
    setNotesOnly(state, val) {
      state.notesOnly = val;
    },
    setRatedOnly(state, val) {
      state.ratedOnly = val;
    },
    setRecentlyAdded(state, val) {
      state.recentlyAdded = val;
    },
    setPreferredCountryOnly(state, val) {
      state.preferredCountryOnly = val;
    },
    setInternationalOnly(state, val) {
      state.internationalOnly = val;
    },
    setDenseFilter(state, val) {
      state.denseFilter = val;
    },
    setDenseListView(state, val) {
      state.denseListView = val;
    },
    setIdToken(state, val) {
      state.idToken = val;
    },
    setHoveredCourseId(state, val) {
      state.hoveredCourseId = val;
    },
    setClickedCourseId(state, val) {
      state.clickedCourseId = val;
    },
    setGeoLocation(state, val) {
      state.geoLocation = val;
    },
    setCourseServiceUser(state, val) {
      state.courseServiceUser = val;
    },
    setWeatherFilterInput(state, val) {
      state.weatherFilterInput = val;
    },
    setWeatherFilterZips(state, val) {
      state.weatherFilterZips = val;
    },
    setValidSubscriptionTerm(state, val) {
      state.validSubscriptionTerm = val;
    },
    setMapBounds(state, val) {
      state.mapBounds = val;
    },
    setSmartZoom(state, val) {
      state.smartZoom = val;
    },
    setCourseRatingAggs(state, val) {
      state.courseRatingAggs = val;
    },
    setUserCourseNotesCourseIds(state, val) {
      state.userCourseNotesCourseIds = val;
    },
    setUserCourseReviewsCourseIds(state, val) {
      state.userCourseReviewsCourseIds = val;
    },
    setMapVisited(state, val) {
      state.mapVisited = val;
    },
  },
  actions: {
    bindUserProfileRef: firestoreAction(({ getters, bindFirestoreRef }) => {
      const docRef = fb.usersCollection.doc(getters.uid);
      // If the document does not exist, it will be created.
      return docRef.set({}, { merge: true }).then(() => {
        // context contains all original properties like commit, state, etc
        // and adds `bindFirestoreRef` and `unbindFirestoreRef`
        // we return the Promise returned by `bindFirestoreRef` that will
        // resolve once data is ready
        return bindFirestoreRef(
          "userProfile",
          fb.usersCollection.doc(getters.uid),
        );
      });
    }),
    fetchApplicationData({ dispatch, commit }) {
      commit("setIsLoading", true);

      return Promise.all([
        dispatch("fetchCourseMetadata"),
        dispatch("fetchCourses"),
        dispatch("fetchCourseRatingAggs"),
      ]).finally(() => {
        commit("setIsLoading", false);
      });
    },
    fetchCourseMetadata({ commit }) {
      return getMetadata().then(metadata => {
        commit("setCourseMetadata", Object.freeze(metadata));
      });
    },
    fetchCourses({ commit }) {
      return getCourses().then(courses => {
        const sorted = sortBy(courses, ["id"]);

        commit("setCoursesFree", Object.freeze(sorted));
      });
    },
    fetchUserProfiles({ commit }) {
      commit("setIsLoading", true);

      return fb.usersCollection
        .get()
        .then(querySnapshot => {
          let profiles = {};

          querySnapshot.docs.forEach(doc => {
            const data = doc.data();
            let loginCount = 0;
            let wishlistCount = 0;
            let coursesPlayedCount = 0;

            if (data.authEvents) {
              loginCount = data.authEvents.filter(ae => ae.name === "login")
                .length;
            }

            if (data.wishlist) {
              wishlistCount = data.wishlist.length;
            }

            if (data.coursePlayed) {
              coursesPlayedCount = data.coursePlayed.length;
            }

            const newProfile = {
              ...data,
              loginCount,
              wishlistCount,
              coursesPlayedCount,
            };

            profiles[doc.id] = {
              ...newProfile,
            };
          });

          return profiles;
        })
        .then(userProfiles => {
          commit("setUserProfiles", userProfiles);
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    updateWishlist({ commit, getters }, id) {
      return new Promise((resolve, reject) => {
        commit("setIsLoading", true);

        // if the wishlist doesn't exist, arrayUnion will create one first
        let newValue = fb.firestore.FieldValue.arrayUnion(id);
        if (getters.wishlist && getters.wishlist.includes(id)) {
          newValue = fb.firestore.FieldValue.arrayRemove(id);
        }
        // If the document does not exist, it will be created.
        // If the document does exist, its contents will be overwritten with the newly provided data,
        // unless you specify that the data should be merged into the existing document.
        const docRef = fb.usersCollection.doc(getters.uid);

        docRef
          .update({
            wishlist: newValue,
          })
          .then(() => {
            console.debug("updateWishlist", id);
            resolve();
          })
          .catch(err => {
            reject(err);
          })
          .finally(() => {
            commit("setIsLoading", false);
          });
      });
    },
    removeFromWishlist({ commit, getters }, id) {
      return new Promise((resolve, reject) => {
        commit("setIsLoading", true);

        if (!getters.wishlist) {
          resolve();
        } else if (!getters.wishlist.includes(id)) {
          resolve();
        }

        let newValue = fb.firestore.FieldValue.arrayRemove(id);

        // If the document does not exist, it will be created.
        // If the document does exist, its contents will be overwritten with the newly provided data,
        // unless you specify that the data should be merged into the existing document.
        const docRef = fb.usersCollection.doc(getters.uid);

        docRef
          .update({
            wishlist: newValue,
          })
          .then(() => {
            console.debug("updateWishlist", id);
            resolve();
          })
          .catch(err => {
            reject(err);
          })
          .finally(() => {
            commit("setIsLoading", false);
          });
      });
    },
    updateCoursePlayed({ commit, dispatch, getters }, id) {
      return new Promise((resolve, reject) => {
        commit("setIsLoading", true);

        // if the wishlist doesn't exist, arrayUnion will create one first
        let newValue = fb.firestore.FieldValue.arrayUnion(id);
        let isUnion = true;

        if (getters.coursePlayed && getters.coursePlayed.includes(id)) {
          newValue = fb.firestore.FieldValue.arrayRemove(id);
          isUnion = false;
        }
        // If the document does not exist, it will be created.
        // If the document does exist, its contents will be overwritten with the newly provided data,
        // unless you specify that the data should be merged into the existing document.
        const docRef = fb.usersCollection.doc(getters.uid);

        docRef
          .update({
            coursePlayed: newValue,
          })
          .then(() => {
            console.debug("coursePlayed", id);

            if (isUnion) {
              return dispatch("removeFromWishlist", id);
            } else {
              return;
            }
          })
          .then(() => {
            resolve();
          })

          .catch(err => {
            reject(err);
          })
          .finally(() => {
            commit("setIsLoading", false);
          });
      });
    },
    updateSavedColumns({ commit, getters }, columns) {
      return new Promise((resolve, reject) => {
        commit("setIsLoading", true);

        const docRef = fb.usersCollection.doc(getters.uid);
        const newValue = columns;

        docRef
          .update({
            savedColumns: newValue,
          })
          .then(() => {
            console.debug("updateSavedColumns", newValue);
            resolve();
          })
          .catch(err => {
            reject(err);
          })
          .finally(() => {
            commit("setIsLoading", false);
          });
      });
    },
    appendAuthEvent({ commit, getters }, name) {
      return new Promise((resolve, reject) => {
        commit("setIsLoading", true);

        const newAuthEvent = {
          name,
          timestamp: new Date(),
        };

        // if the wishlist doesn't exist, arrayUnion will create one first
        let newValue = fb.firestore.FieldValue.arrayUnion(newAuthEvent);

        // If the document does not exist, it will be created.
        // If the document does exist, its contents will be overwritten with the newly provided data,
        // unless you specify that the data should be merged into the existing document.
        const docRef = fb.usersCollection.doc(getters.uid);

        docRef
          .update({
            authEvents: newValue,
          })
          .then(() => {
            console.debug("appendAuthEvent", name, newAuthEvent);
            resolve();
          })
          .catch(err => {
            reject(err);
          })
          .finally(() => {
            commit("setIsLoading", false);
          });
      });
    },
    updateUserProfile({ commit, getters }, profileData) {
      return new Promise((resolve, reject) => {
        commit("setIsLoading", true);

        // If the document does not exist, it will be created.
        // If the document does exist, its contents will be overwritten with the newly provided data,
        // unless you specify that the data should be merged into the existing document (which we do below).
        const docRef = fb.usersCollection.doc(getters.uid);

        docRef
          .set(
            {
              ...profileData,
            },
            { merge: true },
          )
          .then(() => {
            console.debug("updateUserProfile", profileData);
            resolve();
          })
          .catch(err => {
            reject(err);
          })
          .finally(() => {
            commit("setIsLoading", false);
          });
      });
    },
    fetchIdToken({ commit, state }) {
      const currentUser = state.currentUser;

      if (!currentUser) {
        return null;
      }

      commit("setIsLoading", true);

      const forceRefresh = true;
      const email = currentUser.email;

      return currentUser
        .getIdToken(forceRefresh)
        .then(token => {
          commit("setIdToken", token);
        })
        .then(() => {
          logAnalyticsEvent("refreshIdToken", { email });
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchCourseRatingAggs({ commit, state }) {
      commit("setIsLoading", true);

      return getAllAggs()
        .then(results => {
          commit("setCourseRatingAggs", Object.freeze(results));
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchCourseServiceUser({ commit, dispatch, getters, state }) {
      commit("setIsLoading", true);

      const token = state.idToken;
      const uid = getters.uid;

      if (!token) {
        return Promise.reject("token required");
      }

      if (!uid) {
        return Promise.reject("uid required");
      }

      return addOrGetCourseServiceUser(token, uid)
        .then(user => {
          commit("setCourseServiceUser", user);
          commit("setValidSubscriptionTerm", true);

          return dispatch("fetchCoursesByUid").then(() => {
            return user;
          });
        })
        .catch(error => {
          const description = get(
            error,
            "response.data.message",
            "Unspecified error. Please try again or file a feedback issue. Thanks.",
          );
          const message = `Unable to Retrieve Premium Course Data`;
          const caption = description;

          console.error("Error fetching premium course data", message);

          store.commit("setValidSubscriptionTerm", false);

          Notify.create({
            position: "bottom",
            type: "negative",
            timeout: 15000,
            message,
            caption,
          });
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchCoursesByUid({ commit, state }) {
      commit("setIsLoading", true);

      const token = state.idToken;

      return getCoursesByUid(token)
        .then(results => {
          const sorted = sortBy(results, ["id"]);

          commit("setCoursesPremium", Object.freeze(sorted));
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchNotesCourseIdsByUid({ commit, state }) {
      commit("setIsLoading", true);

      const token = state.idToken;

      return getNotesCourseIdsByUid(token)
        .then(results => {
          console.debug("getNotesCourseIdsByUid", results);

          commit("setUserCourseNotesCourseIds", Object.freeze(results));
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchReviewsCourseIdsByUid({ commit, state }) {
      commit("setIsLoading", true);

      const token = state.idToken;

      return getReviewsCourseIdsByUid(token)
        .then(results => {
          console.debug("getReviewsCourseIdsByUid", results);

          commit("setUserCourseReviewsCourseIds", Object.freeze(results));
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    reset({ commit }) {
      commit("setCurrentUser", null);
      commit("setIdToken", null);
      commit("setCoursesPremium", []);
      commit("setFilterInput", []);
      commit("setWishlistOnly", false);
      commit("setRecentlyAdded", false);
      commit("setCoursePlayedOnly", false);
      commit("setNotesOnly", false);
      commit("setRatedOnly", false);
      commit("setPreferredCountryOnly", false);
      commit("setInternationalOnly", false);
      commit("setCourseServiceUser", null);
      commit("setWeatherFilterInput", null);
      commit("setValidSubscriptionTerm", false);
      commit("setSmartZoom", false);
    },
  },
});

const REFRESH_TOKEN_INTERVAL_MINUTES = 50;
const REFRESH_TOKEN_INTERVAL_MILLISECONDS =
  REFRESH_TOKEN_INTERVAL_MINUTES * 60000;
let refreshTokenInterval;

// https://firebase.google.com/docs/reference/js/firebase.auth.Auth#onauthstatechanged
fb.auth.onAuthStateChanged(handleFirebaseAuthStateChanged);

function handleFirebaseAuthStateChanged(user) {
  store.commit("setCurrentUser", user);

  if (!user) {
    clearInterval(refreshTokenInterval);

    return;
  }

  const refreshToken = () => {
    store.dispatch("fetchIdToken");

    refreshTokenInterval = setTimeout(
      refreshToken,
      REFRESH_TOKEN_INTERVAL_MILLISECONDS,
    );
  };

  refreshTokenInterval = setTimeout(
    refreshToken,
    REFRESH_TOKEN_INTERVAL_MILLISECONDS,
  );
}

store.watch(
  state => state.currentUser,
  (newVal, oldVal) => {
    if (!oldVal) {
      console.debug("bindUserProfileRef", newVal, oldVal);
      // if we don't have an oldVal this is the first time currentUser is getting set
      // we only need tp set up our vuex two-way binding once.
      store.dispatch("bindUserProfileRef");
    }

    if (newVal) {
      store
        .dispatch("fetchIdToken")
        .then(() => store.dispatch("fetchCourseServiceUser"))
        .then(() => {
          const promises = [
            store.dispatch("fetchCourseRatingAggs"),
            store.dispatch("fetchNotesCourseIdsByUid"),
            store.dispatch("fetchReviewsCourseIdsByUid"),
          ];

          return Promise.all(promises);
        });
    }
  },
);

// the function we use to apply filters to each course
// the big daddy
function courseFilterFn(element, filterItem) {
  const { id, type, filter } = filterItem;
  const { min, max } = filter;
  let val = element[id];

  if (type === "autocomplete" || type === "checkboxlist") {
    return filter.some(filterInput => val === filterInput);
  } else if (type === "range") {
    val = parseInt(val, 10);
    return val >= min && val <= max;
  } else if (type === "rating_range") {
    val = Number(val);

    // val will be NaN when a course doesn't have ratings
    if (isNaN(val)) {
      // 0 selected in the range includes non-rated courses
      if (min === 0 || max === 0) {
        return true;
      }

      return false;
    } else {
      if (min === 0 && max === 0) {
        return true;
      }

      return val >= min && val <= max;
    }
  }
}

// returns the minimum value in an array
function minValue(items) {
  return Math.min(...items);
}

// returns the maximum value in an array
function maxValue(items) {
  return Math.max(...items);
}

function convertYardToMeter(yard) {
  return yard * 0.9144;
}

function convertMileToKilometer(mile) {
  return mile * 1.609344;
}

function convertFootToMeter(feet) {
  return feet * 0.3048;
}
